import Seo from "@/components/Seo";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { api } from "@/lib/api";
import { siteText } from "@/context/SiteContext";
import { toImageUrl, handleImgError } from "@/lib/driveUrl";
import AgendaSection from "@/components/AgendaSection";
import RichText from "@/components/RichText";

const BADGE_STYLES = ["bg-[#B83227]", "bg-[#E67E22]", "bg-[#1E1B18]", "bg-[#B83227]"];
const BADGE_LETTERS = ["A", "B", "C", "D"];

function safeDate(dateStr) {
  try {
    return format(new Date(dateStr), "d MMMM", { locale: fr }).toUpperCase();
  } catch {
    return "";
  }
}

export default function Home() {
  const [settings, setSettings] = useState(null);
  const [prestations, setPrestations] = useState([]);
  const [news, setNews] = useState([]);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/prestations").then((r) => setPrestations(r.data)).catch(() => {});
    api.get("/news").then((r) => setNews(r.data.slice(0, 3))).catch(() => {});
    api
      .get("/media")
      .then((r) => r.data)
      .catch(() => [])
      .then((media) =>
        api
          .get("/album")
          .then((r) => r.data.items || [])
          .catch(() => [])
          .then((album) => {
            // Photos du groupe uniquement : médiathèque admin + dossier Google Drive public
            const all = [
              ...media.filter((m) => m.kind === "photo").map((m) => ({ url: m.url, caption: m.title || "" })),
              ...album.filter((a) => a.kind === "photo").map((a) => ({ url: a.url, caption: "" })),
            ];
            // 4 photos au hasard, différentes à chaque visite
            setPhotos(all.sort(() => Math.random() - 0.5).slice(0, 4));
          })
      );
  }, []);

  const gallery = photos;

  // Texte de présentation sous la photo : modifiable dans l'admin (champ vidé = masqué)
  const heroCaption =
    settings?.texts && Object.prototype.hasOwnProperty.call(settings.texts, "home_hero_caption")
      ? settings.texts.home_hero_caption
      : "Un collectif de musiciens passionnés — cornemuses du Centre-France, accordéons, chant — qui fait danser bals, fêtes et festivals sur la côte et bien au-delà.";

  return (
    <div>
      <Seo description="Les Sonneurs de la Côte font danser bals, fêtes et festivals : cornemuses du Centre-France, accordéons et chant. Découvrez le groupe, ses photos, ses enregistrements et ses dates." />
      {/* Hero — photo affichée en entier, slogan seul sur une ligne tout en haut */}
      <section data-testid="home-hero-section" className="relative min-h-[220px] border-b border-stone-800 bg-[#1E1B18] text-white sm:min-h-[320px]">
        {settings?.hero_image_url && (
          <img
            src={toImageUrl(settings.hero_image_url)}
            alt="Les Sonneurs de la Côte en concert"
            onError={handleImgError}
            className="block w-full"
          />
        )}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-2/5 bg-gradient-to-b from-black/70 via-black/25 to-transparent" />
        <div className="absolute inset-x-0 top-0 px-4 pt-3 text-center sm:pt-5">
          <h1 className="whitespace-nowrap font-display font-bold uppercase leading-none tracking-tight text-white [font-size:clamp(1.5rem,6.5vw,4.5rem)] [text-shadow:0_2px_20px_rgba(0,0,0,0.7)]">
            {settings?.hero_title || "Faire danser la côte"}
          </h1>
        </div>
        {settings?.hero_image_url && heroCaption && (
          <p data-testid="home-hero-caption" className="relative mx-auto max-w-2xl px-4 py-8 text-center text-sm leading-relaxed text-stone-300 sm:text-base">
            {heroCaption}
          </p>
        )}
      </section>

      {/* Galerie — masquée tant qu'il n'y a pas au moins 4 photos du groupe */}
      {gallery.length >= 4 && (
      <section className="border-b border-stone-200 bg-white py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#B83227]">{siteText(settings, "home_gallery_label", "Instant Cliché")}</span>
              <h2 className="mt-1 font-display text-3xl font-bold uppercase text-[#1E1B18] sm:text-4xl">
                {siteText(settings, "home_gallery_title", "L'Énergie du Direct")}
              </h2>
            </div>
            <p className="max-w-md text-sm text-stone-600">
              {siteText(settings, "home_gallery_text", "De la première bourrée au cercle final, des moments de partage capturés sur le vif.")}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {gallery.map((g, i) => (
              <div key={i} className="relative aspect-square overflow-hidden rounded-xl shadow" data-image-container>
                <img src={toImageUrl(g.url)} alt={g.caption || "Photo des Sonneurs de la Côte"} loading="lazy" onError={handleImgError} className="h-full w-full object-cover" />
                {g.caption && (
                  <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/70 to-transparent p-4">
                    <span className="text-xs font-bold text-white">{g.caption}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-8 text-center">
            <Link to="/galerie" data-testid="home-gallery-link" className="text-xs font-bold uppercase text-[#B83227] hover:underline">
              {siteText(settings, "home_gallery_link", "Voir toute la galerie →")}
            </Link>
          </div>
        </div>
      </section>
      )}

      {/* Prestations */}
      <section className="border-b border-stone-200 bg-[#FAF6F0] py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mx-auto mb-16 max-w-2xl text-center">
            <h2 className="mb-3 font-display text-3xl font-bold uppercase text-[#1E1B18] sm:text-5xl">
              {siteText(settings, "home_prestas_title", "Nos Formules Scéniques")}
            </h2>
            <p className="text-base text-stone-600">
              {siteText(settings, "home_prestas_text", "Des propositions clés en main pour mairies, associations, festivals et événements privés.")}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
            {prestations.map((p, i) => (
              <div
                key={p.id}
                data-testid={`prestations-card-${i === 0 ? "bals" : i === 2 ? "ceremonies" : `item-${i}`}`}
                className="flex flex-col justify-between rounded-2xl border border-stone-200 bg-white p-6 shadow-sm"
              >
                <div>
                  <div
                    className={`mb-4 flex h-10 w-10 items-center justify-center rounded font-display text-lg font-bold text-white ${BADGE_STYLES[i % 4]}`}
                  >
                    {BADGE_LETTERS[i % 4]}
                  </div>
                  <h3 className="mb-2 font-display text-xl font-bold uppercase text-[#1E1B18]">{p.title}</h3>
                  <p className="text-xs leading-relaxed text-stone-600">{p.description}</p>
                </div>
                {p.bookable !== false && (
                  <div className="mt-6 border-t border-stone-100 pt-4">
                    <Link
                      to="/contact"
                      data-testid={`prestation-booking-link-${i}`}
                      className="text-xs font-bold uppercase text-[#B83227] hover:underline"
                    >
                      {siteText(settings, "home_prestas_card_link", "Réserver cette formule →")}
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* L'Ambiance */}
      <section className="border-b border-stone-200 bg-white py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
            <div className="space-y-6 lg:col-span-6">
              <span className="text-xs font-bold uppercase tracking-widest text-[#B83227]">{siteText(settings, "home_ambiance_label", "L'Esprit des Sonneurs")}</span>
              <h2 className="font-display text-3xl font-bold uppercase text-[#1E1B18] sm:text-4xl">
                {siteText(settings, "home_ambiance_title", "Une énergie communicative, du premier au dernier morceau")}
              </h2>
              <RichText
                className="text-justify text-sm leading-relaxed text-stone-700 [hyphens:auto]"
                text={
                  settings?.presentation_text ||
                  "« Les Sonneurs de la Côte » rassemblent des musiciens passionnés qui allient précision instrumentale et générosité de jeu. Sur scène comme au milieu du public, le contact est direct et sans prétention."
                }
              />
              <div className="space-y-2 rounded-xl border border-stone-200 bg-[#FAF6F0] p-4">
                <div className="text-xs font-bold uppercase text-[#B83227]">{siteText(settings, "home_ambiance_box_title", "Notre engagement scénique")}</div>
                <p className="text-xs text-stone-600">
                  {siteText(settings, "home_ambiance_box_text", "Adaptation aux contraintes de chaque lieu, autonomie sonore pour les jauges moyennes et respect des horaires de fête.")}
                </p>
              </div>
            </div>
            <div className="lg:col-span-6">
              <div className="relative aspect-[16/10] overflow-hidden rounded-2xl shadow-lg">
                {settings?.ambiance_image_url ? (
                  <img src={toImageUrl(settings.ambiance_image_url)} onError={handleImgError} alt="Les Sonneurs de la Côte sur scène" loading="lazy" className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-[#1E1B18]" />
                )}
                <div className="absolute inset-0 flex flex-col justify-end bg-gradient-to-t from-black/75 to-transparent p-6 text-white">
                  <span className="font-display text-lg font-bold uppercase">{siteText(settings, "home_ambiance_image_caption", "Ambiance 100% Vivante")}</span>
                  <span className="text-xs text-stone-200">{siteText(settings, "home_ambiance_image_sub", "Des bals inoubliables partagés avec les danseurs")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Agenda / Prochaines dates */}
      <AgendaSection />

      {/* Actualités */}
      <section className="border-b border-stone-200 bg-white py-20">
        <div className="mx-auto max-w-6xl px-4 sm:px-6">
          <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <span className="text-xs font-bold uppercase tracking-widest text-[#B83227]">{siteText(settings, "home_news_label", "La vie du groupe")}</span>
              <h2 className="mt-1 font-display text-3xl font-bold uppercase text-[#1E1B18] sm:text-4xl">
                {siteText(settings, "home_news_title", "Actualités")}
              </h2>
            </div>
            <Link to="/actualites" data-testid="home-news-link" className="text-xs font-bold uppercase text-[#B83227] hover:underline">
              {siteText(settings, "home_news_link", "Toutes les actualités →")}
            </Link>
          </div>
          {news.length === 0 ? (
            <p className="text-sm text-stone-500">Les prochaines dates seront annoncées ici très bientôt.</p>
          ) : (
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {news.map((n, i) => (
                <div key={n.id} data-testid={`home-news-card-${i}`} className="space-y-4 rounded-xl border border-stone-200 bg-white p-6 shadow-sm">
                  <div className="flex items-center justify-between">
                    <span className={`rounded px-2.5 py-1 text-[11px] font-bold uppercase text-white ${BADGE_STYLES[i % 3]}`}>
                      {siteText(settings, "home_news_badge", "Actualité")}
                    </span>
                    <span className="text-xs font-bold text-stone-500">{safeDate(n.date)}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold uppercase text-[#1E1B18]">{n.title}</h3>
                  <p className="line-clamp-3 text-xs leading-relaxed text-stone-600">{n.content}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA final */}
      <section className="bg-[#1E1B18] py-20 text-white">
        <div className="mx-auto max-w-3xl px-4 text-center sm:px-6">
          <h2 className="font-display text-3xl font-bold uppercase sm:text-5xl">{siteText(settings, "home_cta_title", "Envie d'un bal trad chez vous ?")}</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-stone-300">
            {siteText(settings, "home_cta_text", "Mairie, association, festival ou fête privée : racontez-nous votre projet, nous répondons sous 48h.")}
          </p>
          <Link
            to="/contact"
            data-testid="home-final-cta"
            className="mt-8 inline-block rounded bg-[#B83227] px-8 py-4 text-sm font-bold uppercase tracking-wider text-white shadow-lg transition-transform duration-300 hover:scale-[1.02] hover:bg-[#96281E]"
          >
            {siteText(settings, "home_cta_button", "Organiser un événement")}
          </Link>
        </div>
      </section>
    </div>
  );
}
